// Leaderboard client.
//
// Thin, and deliberately incapable of lying. It posts a replay and three
// initials; it cannot post a time, because the API has nowhere to put
// one. Everything on the board was proven by the server re-running the
// stream — see server/verify.ts.
//
// Every call fails soft. A leaderboard is a garnish on a single-player
// game: if the service is down, the run still happened, the PB is still
// yours, and nothing about playing should notice.

import { ENGINE_VERSION } from "./replay";

/** Same-origin on the standalone host so the browser makes no preflight
 *  and no CORS decision; absolute from inside skenOS, which is a
 *  different origin and needs the CSP entry to match. */
export function apiBase(host = typeof location === "undefined" ? "" : location.hostname): string {
  return host === "daemon.skenmy.com" ? "/api" : "https://daemon.skenmy.com/api";
}

export type BoardRow = {
  rank: number;
  id: string;
  initials: string;
  ticks: number;
  nice: number;
  seed: string;
  daily: boolean;
  grazes: number;
  hitsTaken: number;
  upgrades: string[];
  /** Points. Only meaningful on the `score` board, but carried on every
   *  row so a time board can still show what the run was worth. */
  score: number;
  /** Peak load reached, in thousandths. 3400 is `load 3.40`. */
  peakLoadMilli: number;
  submittedAt: string;
  /** The claimed identity, if someone attached this run to a Discord
   *  account. Null is the ordinary case — an unclaimed run is verified
   *  exactly as hard as a claimed one. */
  runner: { name: string; avatarUrl: string | null } | null;
};

export type SubmitResult =
  | {
      ok: true;
      id: string | null;
      duplicate: boolean;
      ticks: number;
      categories: string[];
      /** Shown once, here, and never retrievable again. Write it down.
       *  It is what lets this run be attached to a name later, from a
       *  device that is actually yours — which is the entire point on a
       *  machine shared with a queue of strangers. */
      claimCode: string | null;
    }
  | { ok: false; reason: string };

/** Anything slower than this and the player has moved on. The run is
 *  already saved locally; the board can wait for next time. */
const TIMEOUT_MS = 8000;

async function call<T>(path: string, init?: RequestInit): Promise<T | null> {
  if (typeof fetch !== "function") return null;
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
  try {
    const res = await fetch(`${apiBase()}${path}`, { ...init, signal: ctrl?.signal });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** What the board will accept right now. Worth asking before spending a
 *  run: a client on an older build would have its submission refused
 *  after the fact, which is a miserable way to find out. */
export async function fetchMeta(): Promise<{
  engine: number;
  compatible: boolean;
  claiming: boolean;
} | null> {
  const meta = await call<{ engine: number; claiming?: boolean }>("/meta");
  if (!meta) return null;
  return {
    engine: meta.engine,
    compatible: meta.engine === ENGINE_VERSION,
    // Absent on an older service. Defaulting to false means a stale
    // deploy shows no sign-in button rather than one that fails.
    claiming: meta.claiming === true,
  };
}

export async function fetchBoard(opts: {
  category: string;
  nice?: number;
  seed?: string;
  limit?: number;
  /** Which season. Omitted means the current one. Past seasons are
   *  readable but closed — see `fetchSeasons`. */
  engine?: number;
}): Promise<BoardRow[]> {
  const q = new URLSearchParams({ category: opts.category });
  if (opts.nice !== undefined) q.set("nice", String(opts.nice));
  if (opts.seed) q.set("seed", opts.seed);
  if (opts.limit) q.set("limit", String(opts.limit));
  if (opts.engine !== undefined) q.set("engine", String(opts.engine));
  const out = await call<{ rows: BoardRow[] }>(`/board?${q}`);
  return out?.rows ?? [];
}

/** One engine version's worth of history. */
export type Season = {
  engine: number;
  runs: number;
  opened: string;
  closed: string;
};

/**
 * Every season the board has held.
 *
 * A new engine version starts a season; it does not end the previous
 * one. Old input streams no longer reproduce against the current
 * simulation, so ranking them together would be dishonest — but those
 * runs happened, were verified when they were posted, and are still
 * here. Empty array on failure, like everything else in this client.
 */
export async function fetchSeasons(): Promise<Season[]> {
  const out = await call<{ seasons: Season[] }>("/seasons");
  return out?.seasons ?? [];
}

/** Fetch the proof behind a row, so a claim can be replayed and checked
 *  rather than believed. */
export async function fetchReplay(id: string): Promise<string | null> {
  const out = await call<{ replay: string }>(`/replay/${encodeURIComponent(id)}`);
  return out?.replay ?? null;
}

export async function submitRun(replay: string, initials: string): Promise<SubmitResult> {
  const trimmed = initials.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3);
  if (!trimmed) return { ok: false, reason: "needs at least one letter or digit" };

  // Note what is NOT sent: no time, no category, no difficulty. There is
  // nowhere in this payload to put a claim, which is why the board can
  // be trusted without trusting the client.
  const out = await call<{
    id: string | null;
    duplicate: boolean;
    verified: boolean;
    ticks: number;
    categories: string[];
    claimCode: string | null;
    error?: string;
  }>("/runs", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ replay, initials: trimmed }),
  });

  if (!out) return { ok: false, reason: "the board did not answer — your run is still saved here" };
  if (!out.verified) return { ok: false, reason: out.error ?? "the board would not verify that run" };
  return {
    ok: true,
    id: out.id,
    duplicate: out.duplicate,
    ticks: out.ticks,
    categories: out.categories,
    claimCode: out.claimCode ?? null,
  };
}

// ── Claiming ──────────────────────────────────────────────────────────
//
// Identity is deliberately not part of playing. You submit anonymously,
// and the run is scored, ranked and verified with no account involved.
// Claiming only attaches a name to a time that already stands.
//
// The reason it works this way is the shared PC. At an event you play on
// a machine that is not yours, and every browser-bound identity —
// localStorage token, WebCrypto keypair, a signed-in session — either
// fails to survive the next player or attributes your run to the last
// one. So the event machine holds nothing: it shows a one-time code, and
// you spend that code later, on your own phone, where signing in with
// Discord costs one tap.

/** Cookies are required here and nowhere else in this client — the
 *  session is an httpOnly cookie the page can neither read nor forge. */
const withSession: RequestInit = { credentials: "include" };

export type Me = { name: string } | null;

export async function fetchMe(): Promise<Me> {
  const out = await call<{ runner: { name: string } | null }>("/me", withSession);
  return out?.runner ?? null;
}

/** Where to send the browser to sign in. A full navigation rather than a
 *  fetch: OAuth ends in a redirect back to us, which XHR cannot follow. */
export const signInUrl = (): string => `${apiBase()}/auth/discord`;

export async function signOut(): Promise<void> {
  await call("/auth/logout", { ...withSession, method: "POST" });
}

export type ClaimResult =
  | { ok: true; ticks: number; initials: string; runner: string }
  | { ok: false; reason: string };

export async function claimRun(code: string): Promise<ClaimResult> {
  const out = await call<{
    ok: boolean;
    run: { ticks: number; initials: string };
    runner: { name: string };
  }>("/claim", {
    ...withSession,
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!out?.ok) {
    return {
      ok: false,
      reason: "no unclaimed run with that code — check it, or it may already be claimed",
    };
  }
  return { ok: true, ticks: out.run.ticks, initials: out.run.initials, runner: out.runner.name };
}
